
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Folder, FolderPlus, Trash2 } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "./ui/dialog";
import { Input } from "./ui/input";
import { Card } from "./ui/card";
import { useToast } from "./ui/use-toast";
import { useUser } from "@/hooks/use-user";
import { useNavigate } from "react-router-dom";

type Collection = {
  id: string;
  title: string;
  created_at: string;
  cover_image_url?: string;
  collection_items: {
    id: string;
    post: {
      image_url: string;
    };
  }[];
};

export const CollectionsGrid = () => {
  const navigate = useNavigate();
  const [newCollectionTitle, setNewCollectionTitle] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const { toast } = useToast();
  const { user, isLoading: isUserLoading } = useUser();
  
  const {
    data: collections,
    refetch,
    isLoading
  } = useQuery({
    queryKey: ["collections", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const { data, error } = await supabase
        .from("collections")
        .select(`
          *,
          collection_items (
            id,
            post:posts (
              image_url
            )
          )
        `)
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching collections:", error);
        throw error;
      }
      return data as Collection[];
    },
    enabled: !!user?.id, // Only fetch when user is logged in
  });

  const createCollection = async () => {
    if (!user?.id) {
      toast({
        title: "Erro",
        description: "Você precisa estar logado para criar uma coleção",
        variant: "destructive"
      });
      return;
    }
    
    if (!newCollectionTitle.trim()) {
      toast({
        title: "Erro",
        description: "Por favor, digite um título para a coleção",
        variant: "destructive"
      });
      return;
    }
    
    const { error } = await supabase
      .from("collections")
      .insert([{
        title: newCollectionTitle.trim(),
        user_id: user.id
      }]);
      
    if (error) {
      toast({
        title: "Erro",
        description: "Falha ao criar coleção",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Sucesso",
      description: "Coleção criada com sucesso"
    });
    setNewCollectionTitle("");
    setIsDialogOpen(false);
    refetch();
  };

  const deleteCollection = async (collectionId: string) => {
    const { error } = await supabase
      .from("collections")
      .delete()
      .eq("id", collectionId);
      
    if (error) {
      toast({
        title: "Erro",
        description: "Falha ao excluir coleção",
        variant: "destructive"
      });
      return;
    }
    
    toast({
      title: "Sucesso",
      description: "Coleção excluída com sucesso"
    });
    refetch();
  };

  const navigateToCollection = (collectionId: string) => {
    navigate(`/collections/${collectionId}`);
  };

  if (isUserLoading) {
    return <div className="flex items-center justify-center h-40">Carregando...</div>;
  }

  if (!user) {
    return (
      <div className="flex flex-col items-center justify-center h-40 space-y-4">
        <p>Faça login para ver suas coleções.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Minhas Coleções</h2>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" className="gap-2">
              <FolderPlus className="h-4 w-4" />
              Nova Coleção
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Criar nova coleção</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <Input 
                placeholder="Nome da coleção" 
                value={newCollectionTitle} 
                onChange={e => setNewCollectionTitle(e.target.value)} 
              />
              <Button onClick={createCollection} className="w-full">
                Criar
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <Card key={i} className="h-64 animate-pulse bg-muted"></Card>
          ))}
        </div>
      ) : collections?.length ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
          {collections.map(collection => (
            <Card 
              key={collection.id} 
              className="group relative overflow-hidden p-4 cursor-pointer transition-shadow hover:shadow-md"
              onClick={(e) => {
                // Prevent navigation if clicking on delete button
                if ((e.target as HTMLElement).closest('button')) {
                  return;
                }
                navigateToCollection(collection.id);
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-background/80 to-background/20" />
              <div className="relative z-10">
                <div className="mb-4 flex items-center gap-2">
                  <Folder className="h-5 w-5" />
                  <h3 className="font-semibold">{collection.title}</h3>
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {collection.collection_items?.slice(0, 4).map((item, index) => (
                    <div key={item.id || index} className="aspect-square w-full rounded-sm bg-muted overflow-hidden">
                      {item.post?.image_url ? (
                        <img 
                          src={item.post.image_url} 
                          alt="" 
                          className="h-full w-full object-cover" 
                        />
                      ) : null}
                    </div>
                  ))}
                </div>
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="absolute right-2 top-2 opacity-0 transition-opacity group-hover:opacity-100" 
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCollection(collection.id);
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-40 space-y-4">
          <p className="text-muted-foreground">Você ainda não tem coleções criadas</p>
          <Button onClick={() => setIsDialogOpen(true)} variant="outline" className="gap-2">
            <FolderPlus className="h-4 w-4" />
            Criar primeira coleção
          </Button>
        </div>
      )}
    </div>
  );
};
